import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";

import { CoinsContext } from "../../contexts/CoinsContext";
import { AuthContext } from "../../contexts/AuthContext";
import { LoadingContext } from "../../contexts/LoadingContext";
import { ChannelProvider } from "../../contexts/ChannelContext";

import useSession from "../../hooks/useSession";
import Sidebar from "../main/Sidebar";
import AdminSidebar from "../main/AdminSidebar";
import Header from "../main/Header";
import ToggleButton from "../main/ToggleButton";
import LoadingOverlay from "../main/LoadingOverlay";
import CyberBackground from "../main/CyberBackground";

import { setCookie, getCookie } from "../../utils";
import useSubscription from "../../hooks/useSubscription";
import { useCachedFetch } from "../../hooks/useCachedFetch";
import { PLATFORMS } from "../../constants/platforms";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getCoins, getProfile, getRole } from "../../api/general/api";

// === Подарки отключены ===
// import GiftMemeOverlay from "../overlays/GiftMemeOverlay";

const HEADER_HEIGHT = 72;

function normIsAdmin(raw) {
  if (raw === true) return true;
  if (!raw) return false;
  if (typeof raw === "number") return raw === 1;
  if (typeof raw === "string") {
    const v = raw.trim().toLowerCase();
    return ["1", "true", "yes", "admin"].includes(v);
  }
  if (typeof raw === "object") {
    const u = raw;
    if (u.is_admin === true || u.isAdmin === true) return true;
    if (typeof u.role === "string" && ["admin", "owner", "superadmin"].includes(u.role)) return true;
    if (Array.isArray(u.roles) && u.roles.includes("admin")) return true;
    if (Array.isArray(u.scopes) && u.scopes.includes("admin")) return true;
    if (u.is_admin === 1 || u.isAdmin === 1) return true;
    if (typeof u.is_admin === "string") {
      const v = u.is_admin.trim().toLowerCase();
      if (["1", "true", "yes", "admin"].includes(v)) return true;
    }
    if (typeof u.isAdmin === "string") {
      const v = u.isAdmin.trim().toLowerCase();
      if (["1", "true", "yes", "admin"].includes(v)) return true;
    }
  }
  return false;
}

/* ==== Новый год: Счётчик + салют (оставляем как было) ==== */
function NYEventOverlay() {
  const { pathname } = useLocation();
  const isChannelPage = /(?:^|\/)channel(?:\/|$)/i.test(pathname);

  const [now, setNow] = useState(Date.now());
  const [firing, setFiring] = useState(false);
  const [visible, setVisible] = useState(true);
  const canvasRef = useRef(null);

  const rafRef = useRef(0);
  const burstsRef = useRef([]);
  const stoppedRef = useRef(false);
  const forceRef = useRef(false);

  const bodyHas = (c) =>
    typeof document !== "undefined" ? document.body.classList.contains(c) : false;
  const isOff = () => bodyHas("no-cyber-anim") || bodyHas("ny-off");

  const nextNY = React.useMemo(() => {
    // Фиксированная дата: 01.01.2026 в 00:00:00
    return new Date(2026, 0, 1, 0, 0, 0, 0).getTime();
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    const checkMonth = () => setVisible([11, 0].includes(new Date().getMonth()));
    checkMonth();
    const mm = setInterval(checkMonth, 10000);
    return () => {
      clearInterval(t);
      clearInterval(mm);
    };
  }, []);

  useEffect(() => {
    try {
      // Проверяем, был ли салют уже запущен для 2026 года
      const last = localStorage.getItem("ny_fired_2026");
      if (last === "true") {
        // Если салют уже был запущен и текущее время больше целевой даты, не показываем
        const targetDate = new Date(2026, 0, 1, 0, 0, 0, 0).getTime();
        if (Date.now() > targetDate) {
          stoppedRef.current = true;
        }
      }
    } catch {}
  }, []);

  const diffMs = Math.max(0, nextNY - now);
  const pad = (n) => String(n).padStart(2, "0");

  const dd = pad(Math.floor(diffMs / (24 * 3600_000)));
  const hh = pad(Math.floor((diffMs % (24 * 3600_000)) / 3600_000));
  const mm = pad(Math.floor((diffMs % 3600_000) / 60_000));
  const ss = pad(Math.floor((diffMs % 60_000) / 1000));

  useEffect(() => {
    // Не показываем на channel странице
    if (isChannelPage || stoppedRef.current || isOff()) return;
    // Запускаем салют только когда осталось меньше 1000мс (1 секунда) до 01.01.2026 00:00:00
    // И только если текущая дата меньше целевой даты
    const targetDate = new Date(2026, 0, 1, 0, 0, 0, 0).getTime();
    if (Date.now() < targetDate && diffMs <= 1000 && diffMs > 0 && !firing) {
      forceRef.current = false;
      setFiring(true);
    }
  }, [diffMs, isChannelPage]);

  useEffect(() => {
    if (!firing) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;

    if (!forceRef.current) {
      try {
        localStorage.setItem("ny_fired_2026", "true");
      } catch {}
    }

    const DPR = window.devicePixelRatio || 1;
    const gravity = 0.12;
    const friction = 0.99;

    const W = () => window.innerWidth;
    const H = () => window.innerHeight;

    const onResize = () => {
      const c = canvasRef.current;
      if (!c) return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      c.width = w * DPR;
      c.height = h * DPR;
      c.style.width = w + 'px';
      c.style.height = h + 'px';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(DPR, DPR);
    };

    // Очищаем предыдущие частицы
    burstsRef.current = [];
    
    onResize();
    window.addEventListener("resize", onResize);

    function spawnBurst(x, y) {
      const base = 35;
      const rnd = 35;
      const n = base + Math.floor(Math.random() * rnd);
      
      // Разные типы салютов
      const type = Math.random();
      const isRing = type > 0.7; // Кольцевой салют
      const isStar = type > 0.4 && type <= 0.7; // Звездочка

      for (let i = 0; i < n; i++) {
        let angle, speed, extraLife = 0;
        
        if (isRing) {
          // Кольцевой взрыв
          angle = (Math.PI * 2 * i) / n;
          speed = (2.8 + Math.random() * 4.2) * 1.8;
        } else if (isStar) {
          // Звездочка (5-8 лучей)
          const rays = 6 + Math.floor(Math.random() * 3);
          const ray = i % rays;
          angle = (Math.PI * 2 * ray) / rays + (i / n) * 0.3;
          speed = (2.5 + Math.random() * 4.0) * 1.7;
          extraLife = 15;
        } else {
          // Обычный взрыв
          angle = (Math.PI * 2 * i) / n + Math.random() * 0.15;
          speed = (2.1 + Math.random() * 3.8) * 1.7;
        }

        burstsRef.current.push({
          x,
          y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 1.9 * 1.7,
          life: 75 + Math.random() * 60 + extraLife,
          r: (1.8 + Math.random() * 3.2) * 1.8,
          a: 1,
          hue: (i * 360 / n + Math.random() * 60) % 360, // Начальный цвет
          sparkle: Math.random() > 0.7, // Искрящиеся частицы
        });
      }
    }

    // Первый салют
    const w = W(), h = H();
    if (w > 0 && h > 0) {
      spawnBurst(
        (0.3 + Math.random() * 0.4) * w,
        (0.35 + Math.random() * 0.3) * h
      );
    }

    let start = performance.now();
    let lastSpawn = 0;

    const loop = (t) => {
      rafRef.current = requestAnimationFrame(loop);
      const elapsed = t - start;
      const w = W(),
        h = H();

      // Сброс трансформации для clearRect
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, w * DPR, h * DPR);
      ctx.scale(DPR, DPR);
      ctx.globalCompositeOperation = "lighter";

      // Разные интервалы для разнообразия
      const spawnDelay = Math.random() > 0.3 ? 350 : 420;
      if (elapsed - lastSpawn > spawnDelay) {
        lastSpawn = elapsed;
        spawnBurst(
          (0.1 + Math.random() * 0.8) * w,
          (0.15 + Math.random() * 0.65) * h
        );
      }

      for (let i = burstsRef.current.length - 1; i >= 0; i--) {
        const p = burstsRef.current[i];
        p.vx *= friction;
        p.vy = p.vy * friction + gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.life--;
        p.a *= 0.982;
        
        if (p.life <= 0 || p.a < 0.03) {
          burstsRef.current.splice(i, 1);
          continue;
        }
        
        // Улучшенная отрисовка частицы с градиентами
        ctx.save();
        ctx.globalAlpha = p.a;
        
        // Динамический цвет
        const baseHue = p.hue || ((p.life * 3 + elapsed * 0.15) % 360);
        const saturation = 100;
        const lightness = p.sparkle ? 70 + Math.sin(elapsed * 0.02 + i) * 20 : 55 + Math.random() * 25;
        
        if (p.sparkle && Math.random() > 0.7) {
          // Искрящиеся частицы - ярче и больше
          const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 1.5);
          gradient.addColorStop(0, `hsla(${baseHue}, ${saturation}%, 90%, 1)`);
          gradient.addColorStop(0.5, `hsla(${baseHue}, ${saturation}%, ${lightness}%, 0.8)`);
          gradient.addColorStop(1, `hsla(${baseHue}, ${saturation}%, ${lightness}%, 0)`);
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r * 1.3, 0, Math.PI * 2);
          ctx.fill();
          
          // Дополнительное свечение
          ctx.shadowBlur = 15;
          ctx.shadowColor = `hsl(${baseHue}, ${saturation}%, ${lightness}%)`;
          ctx.fill();
        } else {
          // Обычные частицы с градиентом
          const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r);
          gradient.addColorStop(0, `hsla(${baseHue}, ${saturation}%, ${lightness + 20}%, 1)`);
          gradient.addColorStop(0.6, `hsla(${baseHue}, ${saturation}%, ${lightness}%, 0.9)`);
          gradient.addColorStop(1, `hsla(${baseHue}, ${saturation}%, ${lightness - 10}%, 0)`);
          ctx.fillStyle = gradient;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fill();
        }
        
        // Хвост/след для быстрых частиц
        if (Math.sqrt(p.vx * p.vx + p.vy * p.vy) > 3 && Math.random() > 0.5) {
          ctx.globalAlpha = p.a * 0.4;
          ctx.fillStyle = `hsla(${baseHue}, ${saturation}%, ${lightness}%, 0.5)`;
          ctx.beginPath();
          ctx.arc(p.x - p.vx * 0.3, p.y - p.vy * 0.3, p.r * 0.6, 0, Math.PI * 2);
          ctx.fill();
        }
        
        ctx.restore();
      }

      // Салют длится 90 секунд
      if (elapsed > 90_000 && burstsRef.current.length === 0) stop();
    };

    const stop = () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
      setTimeout(() => setFiring(false), 300);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
    };
  }, [firing]);

  // ТЕСТ САЛЮТА
  useEffect(() => {
    window.SES_TEST_NY = () => {
      // Сбрасываем флаги для принудительного запуска
      stoppedRef.current = false;
      forceRef.current = true;
      setFiring(true);
      console.log('🎆 Салют активирован!');
    };
    return () => delete window.SES_TEST_NY;
  }, []);

  // Показываем счетчик только до 01.01.2026, салют только в момент наступления
  const targetDate = new Date(2026, 0, 1, 0, 0, 0, 0).getTime();
  const isAfterTarget = Date.now() >= targetDate;
  
  // Показываем счетчик только если еще не наступила целевая дата
  const shouldShowCounter = !isChannelPage && !isAfterTarget && visible;
  // Показываем салют только если он запущен
  const shouldShowFireworks = !isChannelPage && firing;
  
  if (!shouldShowCounter && !shouldShowFireworks) return null;

  return (
    <>
      {shouldShowCounter && (
        <div
          className="fixed right-3 pointer-events-none select-none"
          style={{ top: `${HEADER_HEIGHT + 6}px`, zIndex: 5 }}
        >
          <div className="inline-flex items-center gap-2 rounded-2xl bg-black/45 backdrop-blur px-3 py-2 text-xs md:text-sm font-medium text-white shadow-lg">
            <span className="opacity-80">🎄 До Нового года:</span>
            <span className="tabular-nums font-semibold">
              {dd}д : {hh}ч : {mm}м : {ss}с
            </span>
          </div>
        </div>
      )}

      {shouldShowFireworks && (
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 4 }}>
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
          {/* Улучшенный градиент для атмосферы */}
          <div className="absolute inset-0 bg-gradient-to-t from-purple-900/20 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20" />
        </div>
      )}
    </>
  );
}

/* === Основной Layout === */
const Layout = () => {
  const { logout } = useContext(AuthContext);
  const { showLoading, hideLoading } = useContext(LoadingContext);

  useSession();

  const [userCoins, setUserCoins] = useState(0);
  const [platform, setPlatform] = useState(PLATFORMS[0].id);
  const [username, setUsername] = useState("");
  const [avatar, setAvatar] = useState("default-avatar.png");
  const [isAdmin, setIsAdmin] = useState(false);

  const [sidebarVisible, setSide] = useState(false);
  const [isTransitioning, setTrans] = useState(false);

  const audioRef = useRef(null);

  const { subscription, checkSubscription } = useSubscription();
  const cached = useCachedFetch(30000);

  const navigate = useNavigate();
  const { platform: routePlat } = useParams();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  useEffect(() => {
    setSide(false);
  }, [location.pathname]);

  const fetchUserCoins = useCallback(
    () =>
      cached("coins", async () => {
        showLoading("Монеты…");
        const { data } = await getCoins();
        setUserCoins(data.coins);
        hideLoading();
        return data;
      }),
    [cached, showLoading, hideLoading]
  );

  const fetchProfile = useCallback(
    () =>
      cached("profile", async () => {
        showLoading("Профиль…");
        const { data } = await getProfile();
        if (data.username) setUsername(data.username);
        if (data.avatar) setAvatar(data.avatar);
        hideLoading();
        return data;
      }),
    [cached, showLoading, hideLoading]
  );

  const setupAdmin = useCallback(
    () =>
      cached("role", async () => {
        showLoading("Роль…");
        const { data } = await getRole();
        setIsAdmin(normIsAdmin(data));
        hideLoading();
        return data;
      }),
    [cached, showLoading, hideLoading]
  );

  useEffect(() => {
    const saved = getCookie("platform");
    const initial =
      saved && PLATFORMS.some((p) => p.id === saved)
        ? saved
        : PLATFORMS[0].id;

    setPlatform(initial);

    Promise.all([
      fetchUserCoins(),
      fetchProfile(),
      checkSubscription(),
      setupAdmin(),
    ]);
  }, []);

  useEffect(() => {
    if (routePlat && PLATFORMS.some((p) => p.id === routePlat)) {
      setPlatform(routePlat);
      setCookie("platform", routePlat, 365);
    }
  }, [routePlat]);

  const handlePlatformSwitch = (id) => {
    if (id === platform || isTransitioning) return;

    setTrans(true);
    showLoading("Смена платформы…");

    localStorage.removeItem("channel_id");
    localStorage.removeItem("selectedGroupId");

    setTimeout(() => {
      setPlatform(id);
      setCookie("platform", id, 365);
      setTrans(false);
      hideLoading();
      navigate("/feed");
    }, 800);
  };

  const themeBackground =
    "linear-gradient(135deg, rgba(59, 7, 100, 1) 0%, rgba(0, 0, 0, 1) 47%, rgba(0, 0, 0, 1) 100%)";
  const themeBoxShadow = "0px 4px 12px 0px rgba(0, 0, 0, 0.15)";
  const pagePaddingTop = `${HEADER_HEIGHT}px`;
  const showAdminSidebar = isAdminRoute && isAdmin;

  const subsForHeader = subscription?.data || subscription;

  return (
    <CoinsContext.Provider
      value={{ coins: userCoins, setCoins: setUserCoins }}
    >
      <ChannelProvider platform={platform}>
        <div
          className={`relative min-h-screen w-full overflow-hidden text-white transition-all ${
            isTransitioning ? "opacity-80 blur-[1px]" : ""
          }`}
          style={{ background: themeBackground, boxShadow: themeBoxShadow }}
        >
          <ToggleButton
            open={sidebarVisible}
            onClick={() => setSide((v) => !v)}
          />

          <div
            className={`fixed inset-0 z-30 bg-black/40 transition-opacity duration-300 ${
              sidebarVisible
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none"
            }`}
            onClick={() => setSide(false)}
          />

          {showAdminSidebar ? (
            <AdminSidebar
              visible={sidebarVisible}
              onClose={() => setSide(false)}
            />
          ) : (
            <Sidebar
              platform={platform}
              onPlatformSwitch={handlePlatformSwitch}
              subscription={subsForHeader}
              isAdmin={isAdmin}
              visible={sidebarVisible}
              onClose={() => setSide(false)}
            />
          )}

          {/* Фон */}
          <CyberBackground key={platform} platform={platform} />

          {/* Header */}
          <Header
            platform={platform}
            subscription={subscription?.data || subscription}
            username={username}
            userCoins={userCoins}
            audioRef={audioRef}
            onLogout={logout}
          />

          {/* Салют + счётчик */}
          <NYEventOverlay />

          {/* Подарки отключены */}
          {/* <GiftMemeOverlay /> */}

          <main
            className="content relative z-10 w-11/12 mx-auto pb-6"
            style={{ paddingTop: pagePaddingTop }}
          >
            <Outlet
              context={{
                currentUsername: username,
                currentAvatar: avatar,
                isSiteAdmin: isAdmin,
              }}
            />
          </main>

          <LoadingOverlay />
          <ToastContainer position="top-right" autoClose={3000} />
        </div>
      </ChannelProvider>
    </CoinsContext.Provider>
  );
};

export default Layout;
